<script lang="ts">
import LineChart, { type LineDataset } from '../charts/LineChart.svelte'

/**
 * “Market growth” block: `.ct` / `.cs`, {@link LineChart}, and the three-column summary row
 * (first period · total growth % · latest period) used on Chapter 01 slides.
 */
interface Props {
  active: boolean
  labels: string[]
  datasets: LineDataset[]
  chartTitle: string
  chartSubtitle: string
  /** Left column main line (e.g. formatted claims for the first quarter). */
  firstValue: string
  /** Left column caption (e.g. quarter label). */
  firstLabel: string
  /** Center column — typically `+{growthPct}%` from the parent. Omit to drop the centre cell
   *  entirely, leaving first and latest as a plain before/after pair. */
  growthPctDisplay?: string
  /** Caption under the growth figure. Pass '' to hide it when the figure is self-describing
   *  (e.g. a bare multiple like `574×`, where the word "growth" adds nothing). */
  growthCaption?: string
  /** Right column main line (e.g. formatted claims for the latest quarter). */
  latestValue: string
  /** Right column caption (e.g. quarter label). */
  latestLabel: string
  height?: string
  yTickFormat?: (v: number) => string
  tooltipFormat?: (value: number, datasetLabel: string) => string
  maxTicksLimit?: number
  /** Show the chart legend. Needed when the card plots more than one series — a
   *  single-series growth chart is self-describing, so this defaults off and every
   *  existing caller is unaffected. */
  showLegend?: boolean
  /** Full-strength legend text rather than the muted default — for when telling the series
   *  apart is the point of the chart. */
  strongLegend?: boolean
}

let {
  active,
  labels,
  datasets,
  chartTitle,
  chartSubtitle,
  firstValue,
  firstLabel,
  growthPctDisplay,
  growthCaption = 'Total Growth',
  latestValue,
  latestLabel,
  height = '210px',
  yTickFormat,
  tooltipFormat,
  maxTicksLimit,
  showLegend = false,
  strongLegend = false,
}: Props = $props()
</script>

<div class="ct">{chartTitle}</div>
<div class="cs">{chartSubtitle}</div>
<LineChart
  {active}
  {height}
  {labels}
  {datasets}
  {yTickFormat}
  {tooltipFormat}
  {maxTicksLimit}
  {showLegend}
  {strongLegend}
/>
<div class="line-chart-growth-card__summary">
  <div class="line-chart-growth-card__cell">
    <div class="line-chart-growth-card__value line-chart-growth-card__value--primary">
      {firstValue}
    </div>
    <div class="line-chart-growth-card__caption">{firstLabel}</div>
  </div>
  {#if growthPctDisplay}
    <div class="line-chart-growth-card__cell">
      <div class="line-chart-growth-card__value line-chart-growth-card__value--growth">
        {growthPctDisplay}
      </div>
      {#if growthCaption}
        <div class="line-chart-growth-card__caption">{growthCaption}</div>
      {/if}
    </div>
  {/if}
  <div class="line-chart-growth-card__cell">
    <div class="line-chart-growth-card__value line-chart-growth-card__value--primary">
      {latestValue}
    </div>
    <div class="line-chart-growth-card__caption">{latestLabel}</div>
  </div>
</div>

<style>
.line-chart-growth-card__summary {
  display: flex;
  justify-content: space-between;
  padding: 0.55rem 0.2rem 0;
}
.line-chart-growth-card__cell {
  text-align: center;
}
.line-chart-growth-card__value {
  font-family: 'DM Serif Display', serif;
  font-size: 1.05rem;
}
.line-chart-growth-card__value--primary {
  color: var(--g);
}
.line-chart-growth-card__value--growth {
  color: var(--amb);
}
.line-chart-growth-card__caption {
  font-size: 0.58rem;
  color: var(--mu);
}
</style>
